import React, { useState, useEffect } from 'react';
import {
  FiUpload,
  FiDownload,
  FiRefreshCw,
  FiPackage,
  FiHash,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  getAOSPVersion,
  uploadAOSPApk,
  updateAOSPVersion,
  getAOSPApkUrl,
  getAOSPVersionUrl,
} from '../utils/wasabiMusicDJUploader';
import { formatBytes } from './AppsPage';

export default function AOSPUpdaterPage() {
  const [currentVersion, setCurrentVersion] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(true);
  const [apkFile, setApkFile] = useState(null);
  const [newVersion, setNewVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch current version on mount
  useEffect(() => {
    fetchCurrentVersion();
  }, []);

  const fetchCurrentVersion = async () => {
    setLoadingVersion(true);
    try {
      const version = await getAOSPVersion();
      setCurrentVersion(version);
      // Suggest next version (increment last number)
      const parts = version.split('.');
      if (parts.length > 0) {
        const lastPart = parseInt(parts[parts.length - 1], 10);
        if (!isNaN(lastPart)) {
          parts[parts.length - 1] = (lastPart + 1).toString();
          setNewVersion(parts.join('.'));
        } else {
          setNewVersion((parseInt(version, 10) + 1).toString());
        }
      } else {
        setNewVersion((parseInt(version, 10) + 1).toString());
      }
    } catch (error) {
      toast.error('Failed to fetch current version: ' + error.message);
      setCurrentVersion('Unknown');
      setNewVersion('1');
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.apk')) {
        toast.error('Please select an APK file');
        return;
      }
      setApkFile(file);
    }
  };

  const handleUpload = async () => {
    if (!apkFile) {
      toast.error('Please select an APK file to upload');
      return;
    }

    if (!newVersion || newVersion.trim() === '') {
      toast.error('Please enter a version number');
      return;
    }

    // Validate version format (simple check)
    const versionNum = parseFloat(newVersion);
    if (isNaN(versionNum) || versionNum <= 0) {
      toast.error('Please enter a valid version number');
      return;
    }

    // Check if new version is greater than current
    if (currentVersion && currentVersion !== 'Unknown') {
      const currentNum = parseFloat(currentVersion);
      if (!isNaN(currentNum) && versionNum <= currentNum) {
        toast.warning(`New version (${newVersion}) should be greater than current version (${currentVersion})`);
      }
    }

    setUploading(true);
    try {
      // Step 1: Upload APK
      toast.info('Uploading APK...');
      await uploadAOSPApk(apkFile);
      toast.success('APK uploaded successfully!');

      // Step 2: Update version.txt
      toast.info('Updating version number...');
      await updateAOSPVersion(newVersion.trim());
      toast.success(`Version updated to ${newVersion}`);

      // Step 3: Refresh state
      setCurrentVersion(newVersion.trim());
      setApkFile(null);
      setLastUpdated(new Date());

      // Suggest next version
      const parts = newVersion.trim().split('.');
      if (parts.length > 0) {
        const lastPart = parseInt(parts[parts.length - 1], 10);
        if (!isNaN(lastPart)) {
          parts[parts.length - 1] = (lastPart + 1).toString();
          setNewVersion(parts.join('.'));
        } else {
          setNewVersion((parseInt(newVersion, 10) + 1).toString());
        }
      }

      // Reset file input
      const fileInput = document.getElementById('apk-file-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      toast.error('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateVersionOnly = async () => {
    if (!newVersion || newVersion.trim() === '') {
      toast.error('Please enter a version number');
      return;
    }

    setUploading(true);
    try {
      await updateAOSPVersion(newVersion.trim());
      toast.success(`Version updated to ${newVersion}`);
      setCurrentVersion(newVersion.trim());
      setLastUpdated(new Date());

      // Suggest next version
      const parts = newVersion.trim().split('.');
      if (parts.length > 0) {
        const lastPart = parseInt(parts[parts.length - 1], 10);
        if (!isNaN(lastPart)) {
          parts[parts.length - 1] = (lastPart + 1).toString();
          setNewVersion(parts.join('.'));
        }
      }
    } catch (error) {
      toast.error('Failed to update version: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page-shell">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {/* Page header — brand icon badge + title, matching dashboard's
          PanelCard header rhythm. */}
      <div className="flex items-center gap-3 mb-6">
        <div className="brand-icon-badge"><FiPackage /></div>
        <h1 className="text-xl font-semibold text-white">AOSP Updater</h1>
      </div>

      {/* Current Status */}
      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><FiCheckCircle /></div>
          <h2 className="text-sm font-semibold text-white">Current Status</h2>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* Current Version */}
          <div className="card-inset p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <FiHash size={14} />
              <span className="text-xs uppercase tracking-wider">Current Version</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold text-white">
                {loadingVersion ? (
                  <span className="animate-pulse text-gray-500">Loading…</span>
                ) : (
                  currentVersion || 'Unknown'
                )}
              </span>
              <button
                onClick={fetchCurrentVersion}
                disabled={loadingVersion}
                className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.06] transition"
                title="Refresh version"
              >
                <FiRefreshCw className={loadingVersion ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* APK Download Link */}
          <div className="card-inset p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <FiDownload size={14} />
              <span className="text-xs uppercase tracking-wider">APK Download</span>
            </div>
            <a
              href={getAOSPApkUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-400 hover:text-brand text-sm break-all"
            >
              app-aosp-release.apk
            </a>
          </div>

          {/* Version File Link */}
          <div className="card-inset p-4">
            <div className="flex items-center gap-2 text-gray-400 mb-2">
              <FiHash size={14} />
              <span className="text-xs uppercase tracking-wider">Version File</span>
            </div>
            <a
              href={getAOSPVersionUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-400 hover:text-brand text-sm break-all"
            >
              app-aosp-version.txt
            </a>
          </div>
        </div>

        {lastUpdated && (
          <p className="mt-4 text-xs text-gray-500">
            Last updated: {lastUpdated.toLocaleString()}
          </p>
        )}
      </section>

      {/* Upload */}
      <section className="card-chrome mb-4">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><FiUpload /></div>
          <h2 className="text-sm font-semibold text-white">Upload New APK</h2>
        </header>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left — version input + "version only" action */}
          <div className="flex-1 space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-400 mb-1.5">
                New Version Number
              </label>
              <input
                type="text"
                placeholder="e.g. 1.2.3 or 42"
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                className="input-field"
              />
              <p className="text-xs text-gray-500 mt-1.5">
                Current: <span className="text-gray-300">{currentVersion || '?'}</span>
                {' → '}
                New: <span className="text-gray-300">{newVersion || '?'}</span>
              </p>
            </div>

            <button
              onClick={handleUpdateVersionOnly}
              disabled={uploading || !newVersion}
              className="btn-secondary w-full"
            >
              Update Version Only (no APK)
            </button>
          </div>

          {/* Right — file picker + primary upload action */}
          <div className="flex flex-col items-center justify-center lg:w-1/2 space-y-3">
            <label className="btn-secondary cursor-pointer w-full max-w-xs">
              <FiUpload /> Choose APK File
              <input
                id="apk-file-input"
                type="file"
                accept=".apk"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>

            {apkFile && (
              <div className="text-xs text-gray-400 space-y-0.5 text-center">
                <p>Selected: <span className="text-white font-medium">{apkFile.name}</span></p>
                <p>Size: <span className="text-white font-medium">{formatBytes(apkFile.size)}</span></p>
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={uploading || !apkFile}
              className="btn-primary w-full max-w-xs"
            >
              {uploading ? (
                <>
                  <FiRefreshCw className="animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <FiUpload /> Upload APK &amp; Update Version
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Instructions */}
      <section className="card-chrome">
        <header className="flex items-center gap-3 mb-4">
          <div className="brand-icon-badge"><FiAlertCircle /></div>
          <h2 className="text-sm font-semibold text-white">How It Works</h2>
        </header>

        <ol className="text-sm text-gray-300 space-y-2 list-decimal list-inside marker:text-gray-500">
          <li>The <span className="text-white">MusicDJ Updater</span> app on devices checks the version file periodically.</li>
          <li>When a new version is detected, devices download and install the new APK automatically.</li>
          <li>Use <span className="text-white">Upload APK &amp; Update Version</span> to upload a new APK and increment the version.</li>
          <li>Use <span className="text-white">Update Version Only</span> if the APK is already uploaded and you just need to trigger updates.</li>
        </ol>

        <div className="mt-4 card-inset p-4">
          <h3 className="text-xs uppercase tracking-wider text-gray-400 mb-2">Wasabi Storage URLs</h3>
          <div className="text-xs text-gray-400 space-y-1 font-mono break-all">
            <p>APK: <span className="text-brand-400">{getAOSPApkUrl()}</span></p>
            <p>Version: <span className="text-brand-400">{getAOSPVersionUrl()}</span></p>
          </div>
        </div>
      </section>
    </div>
  );
}
